import { get, isEmpty, lowerCase } from 'lodash';
import dayjs from 'dayjs';
import {
  AprConfig,
  AprConfigWithCondition,
  CarPriceRule,
  PriceRule,
  PriceRulesEntry,
} from './legos/dealerTypes';
import { Deal } from './legos/dealTypes';
import { Car, CarBody } from './legos/carTypes';
import { PriceRules } from './legos/priceRulesTypes';
import { Condition } from './legos/constants';
import { CLIENT_TYPE, PriceRuleOffsetType, PriceRuleSource } from './constants';

export const PRODIGY_VIN_PREFIX = 'PDGY';

interface TrimEntryLike {
  trim: string;
  mfrModelCodes?: {
    code: string;
    name: string;
  }[];
}

function isDefined<T>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null;
}

/**
 * Checks whether the given car matches the given chromedata model name
 */
export const matchChromeDataModel = <
  C extends {
    model?: string | null;
    pricingMatrixModel?: string;
    body?: CarBody | null;
  }
>(
  car: C,
  chromedataModel: string,
) => {
  if (isEmpty(car.model)) {
    return false;
  }

  const carModel = car.model?.toLowerCase();
  const chromeModel = chromedataModel.toLowerCase();

  if (car?.pricingMatrixModel?.toLowerCase() === chromeModel) {
    return true;
  }

  if (carModel === chromeModel) {
    return true;
  }

  // chromedata may include the body style in the model name they use
  return `${carModel} ${car.body?.toLowerCase()}` === chromeModel;
};

export const isCarReserved = (car: Car | undefined) => !!car?.Deposits?.id;

export const isCarReservedByDeal = (car: Car, deal: Deal) =>
  isCarReserved(car) &&
  !!deal?.deposit_id &&
  deal.deposit_id === get(car, 'Deposits.id');

const escapeRegex = (str: string) =>
  str.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

const matchByExactTrim = <T extends TrimEntryLike>(
  nonNullTrims: T[],
  carTrim: string,
): T | undefined => {
  const exactTrim = nonNullTrims.find(
    trimEntry =>
      trimEntry.trim.toLowerCase() === carTrim.toLowerCase() ||
      !!trimEntry.mfrModelCodes?.find(
        (modelCode: { name: string }) =>
          modelCode.name.toLowerCase() === carTrim.toLowerCase() ||
          modelCode.name
            .replace(/\(.*\)/gi, '')
            .trim()
            .toLowerCase() === carTrim?.toLowerCase(),
      ),
  );
  return exactTrim;
};

const matchByModelCode = <T extends TrimEntryLike>(
  nonNullTrims: T[],
  car: Car,
): T | undefined => {
  const modelCodeTrimMatch = nonNullTrims.find(trimEntry => {
    return trimEntry.mfrModelCodes?.find(
      (modelCode: { code: string | null | undefined }) =>
        modelCode.code === car.modelCode,
    );
  });

  return modelCodeTrimMatch;
};

const matchByPartialTrim = <T extends TrimEntryLike>(
  nonNullTrims: T[],
  trim: string,
): T | undefined => {
  const trimMatches = nonNullTrims.find(
    trimEntry =>
      new RegExp(`^${escapeRegex(trimEntry.trim)}\\b`, 'i').exec(
        trim.toLowerCase() || '',
      ) !== null,
  );
  return trimMatches;
};

/**
 * Matches a car to a trim entry in the given list using whole word match
 */
export const matchCarToTrimEntry = <T extends TrimEntryLike>(
  trims: T[],
  car: Car,
): T | undefined => {
  const nonNullTrims = trims.filter(trimEntry => trimEntry.trim);

  // Try to do a match by comparing model code.
  const matchedByModelCode = matchByModelCode(nonNullTrims, car);
  if (matchedByModelCode) {
    return matchedByModelCode;
  }

  if (!car.trim) {
    return undefined;
  }

  // IF model code not matched, try to get an exact match, ignoring parentheses if necessary
  // for example `LX`, `LX CVT` and `LX CVT (Natl)` should all match an entry with
  // trim `LX` and mfrModelCode name `LX CVT (Natl)`
  const matchedByExactTrim = matchByExactTrim(nonNullTrims, car.trim);
  if (matchedByExactTrim) {
    return matchedByExactTrim;
  }

  // If we can get the model code to match we return the longest partial trim match
  return matchByPartialTrim(nonNullTrims, car.trim);
};

/**
 * Returns the given price rule if it's not expired, otherwise returns undefiend
 */
const ifNotExpired = (priceRule: CarPriceRule | undefined) =>
  !isDefined(priceRule) ||
  !isDefined(priceRule.expiration) ||
  dayjs(priceRule.expiration).isAfter(dayjs())
    ? priceRule
    : undefined;

const getCarPriceRuleForUseVehicles = (
  priceRules: PriceRulesEntry | undefined,
  car: Car,
): PriceRule | CarPriceRule => {
  const defaultRules = priceRules?.defaultRule ?? {
    priceSource: PriceRuleSource.FEED_PRICE,
    offsetType: PriceRuleOffsetType.ADD, // this should be ignored since there's no offsetValue
  };
  const year = car.year ?? 0;
  const usedCarRule = priceRules?.carRules?.find(
    r => r.maxYear && r.minYear && year <= r.maxYear && year >= r.minYear,
  );
  return ifNotExpired(usedCarRule) ?? defaultRules;
};
/**
 * Gets the price rule for the given car
 */
export const getCarPriceRule = (
  priceRules: PriceRulesEntry | undefined,
  car: Car,
): PriceRule | CarPriceRule => {
  if (car.condition === Condition.USED) {
    return getCarPriceRuleForUseVehicles(priceRules, car);
  }
  const carRules = priceRules?.carRules?.filter(
    r =>
      r.year === car.year &&
      lowerCase(r.make) === lowerCase(car.make || '') &&
      r.model &&
      matchChromeDataModel(car, r.model),
  );
  const modelRule = carRules?.find(r => r.trim === undefined);
  const trimRule = matchCarToTrimEntry(
    carRules?.filter(
      (r): r is CarPriceRule & { trim: string } =>
        isDefined(r.trim) && !isDefined(r.vin), // Get Rules that does have trim, but does not have vin
    ) || [],
    car,
  );
  const vinRule = car.id ? carRules?.find(r => r.vin === car.id) : undefined;

  // get the best rule for this car
  return (
    ifNotExpired(vinRule) ??
    ifNotExpired(trimRule) ??
    ifNotExpired(modelRule) ??
    priceRules?.defaultRule ?? {
      priceSource: PriceRuleSource.FEED_PRICE,
      offsetType: PriceRuleOffsetType.ADD, // this should be ignored since there's no offsetValue
    }
  );
};

export const getAprConfigForCar = (
  car: Car,
  globalAprConfig: AprConfigWithCondition | null,
  dealerPriceRules: PriceRules | null,
  clientType: CLIENT_TYPE,
  condition: Condition,
): AprConfig | undefined => {
  // First try to extract vehicle specific APR config from the Pricing Rules and see if it's enabled
  const clientPriceRules = dealerPriceRules?.data?.[clientType]?.[condition];
  const priceRule = getCarPriceRule(clientPriceRules, car);
  if (priceRule.aprConfig?.enabled) {
    return priceRule.aprConfig;
  }

  // Fall back to the global dealer apr configuration and see if it's enabled
  if (globalAprConfig?.[condition]?.enabled) {
    return globalAprConfig[condition];
  }

  // If CPO and there are no prilerule configs, Fall back to the global dealer apr configuration for new cars and see if it's enabled
  if (
    condition === Condition.CPO &&
    globalAprConfig?.[Condition.NEW]?.enabled
  ) {
    return globalAprConfig[Condition.NEW];
  }
  // Otherwise return nothing
  return undefined;
};

// VIN is 17 chars long
// PDGY is 4 chars
// Unix Timestamp is 13 chars
// Used when adding vehicle without VIN, so we can desk a deal with it
export const generateProdigyVIN = () => `${PRODIGY_VIN_PREFIX}${Date.now()}`;

// Valid VIN: PDGY1234567890123
export const isValidProdigyVIN = (vin: string) => {
  const regex = new RegExp(`^${PRODIGY_VIN_PREFIX}\\d{13}$`);
  return !!vin?.match(regex);
};

export const BYO_VIN_PREFIX = 'BYOC';

export const isValidByoVIN = (vin?: string) =>
  !!vin?.match(new RegExp(`^${BYO_VIN_PREFIX}\\d{13}$`));

// When it's Prodigy VIN, return 'N/A'
export const getVINForDisplay = (vin: string) =>
  isValidProdigyVIN(vin) || isValidByoVIN(vin) ? 'N/A' : vin;

// Returns car.name if defined, otherwise returns YMMT
export const getVehicleName = (car: Car) => {
  let name = get(car, 'name');
  if (!name) {
    name = ['year', 'make', 'model', 'trim']
      .filter(field => get(car, field))
      .map(field => get(car, field))
      .join(' ');
  }
  return name;
};

export const getCarYearStatus = ({
  car,
  availableCarsYears,
  availableCarsConfig,
}: {
  car: Car;
  availableCarsYears: {
    nextYear: number;
    currentYear: number;
    lastYear: number;
    yearBeforeLast: number;
  };
  availableCarsConfig?: {
    nextYear?: boolean;
    currentYear?: boolean;
    lastYear?: boolean;
    yearBeforeLast?: boolean;
  };
}): boolean => {
  const includedCarYear = Object.values(availableCarsYears).includes(
    car.year || 0,
  );

  if (!includedCarYear) {
    return false;
  }

  let yearSatus;
  Object.entries(availableCarsYears).forEach(([st, year]) => {
    if (car.year === year) {
      yearSatus = st;
    }
  });

  if (!yearSatus) {
    return false;
  }

  if (!availableCarsConfig) {
    return false;
  }

  return availableCarsConfig[`${yearSatus}`];
};
